import React, { useEffect } from "react";
import { connect } from "react-redux";
import Card from "components/card";
import { Form, Input, DatePicker, Select, InputNumber } from 'antd';
import dayjs from 'dayjs';
import FormItem from "components/fields/FormItem";
import { 
	getInvoiceDetails, doInvoiceCreate, doInvoiceUpdate 
} from "../../../../redux/invoice/invoiceState";
import { useForm } from 'antd/lib/form/Form';
import { requiredField } from '../../../../utils/crudSagaHelper';
import { NumberFormatter, formatDateTimeOrNull } from "../../../../utils/index";
import CrudButtons from "components/button/crudbuttons";
import { MODULE_INVOICES, DATE_FORMAT } from "variables/constants";
import { getForexList } from "../../../../redux/optionlist/optionlistState";

const InvoiceForm = ({getRecord, doCreate, doUpdate, recordData, requesting, errors, onCancel, submitLabel, getForexOptions, forexList, ...props}) => {
	
  	const { recordId, mode } = props;  	
  	const [form] = useForm();
  	
  	useEffect(() => {
        getRecord(recordId ?? 0);
	}, [getRecord, recordId]);

	useEffect(() => {
		getForexOptions({asOptions: 1});
	}, [getForexOptions]);
	
	useEffect(() => {
		if(recordData){

			const { inv_issue_date, inv_due_date, created_at, ...rest } = recordData;
	        const initialValues = {
	            ...rest,
	            inv_issue_date: inv_issue_date ? dayjs(inv_issue_date, DATE_FORMAT) : null,
				created_at: created_at ? dayjs(created_at, DATE_FORMAT) : null,
			    inv_due_date: inv_due_date ? dayjs(inv_due_date, DATE_FORMAT) : null,
	        };
	        
	        form.setFieldsValue(initialValues);
        }
    }, [recordData, form]);
    
  	const onSubmit = (values) => {	
    	try {  			
			const { inv_issue_date, inv_due_date, created_at, ...forSubmitValues } = values;			  
			forSubmitValues.inv_issue_date = inv_issue_date ? inv_issue_date.format(DATE_FORMAT) : null;
			forSubmitValues.inv_due_date = inv_due_date ? inv_due_date.format(DATE_FORMAT) : null;
			forSubmitValues.created_at = created_at ? created_at.format(DATE_FORMAT) : null;
			recordData.inv_id > 0 ? doUpdate(forSubmitValues): doCreate(forSubmitValues); 
    	} catch (error) {
      		console.error('Save failed:', error);
    	}
  	};

	const forexOptions = [
		{
			value: 0,
			label: "Manual",
		},
		...forexList,
	];

	const handleForexChange = (value) => {
		if(value === 0) {
			form.setFieldsValue({
				forex_usd: null,
				forex_aud: null,
			});
		}else{
			const selectedForex = forexList.find(forex => Number(forex.value) === Number(value));
			console.log('Selected Forex:', selectedForex);
			if(selectedForex) {
				form.setFieldsValue({
					forex_usd: Number(selectedForex.usd),
					forex_aud: Number(selectedForex.aud),
				});
			}
		}
	};
  
  	return (
    	<Card extra={"w-full h-full p-4"}>
		  	<Form form={form} className="p-4 bg-white rounded-md dark:bg-intsamis-900"
		  		layout="vertical"
		  		onFinish={onSubmit}
	        >   
	        	<FormItem
			        name="inv_id"
			        hidden
			    >
			        <Input type="hidden"/>
			    </FormItem>
			    <FormItem
			        name="inv_account_id"
			        hidden
			    >
			        <Input type="hidden"/>
			    </FormItem>			    
        		<div className="grid grid-cols-1 md:grid-cols-5 gap-2"> 
					<FormItem 
		            	name="inv_status_id"
		                label="Status"
						rules={[requiredField()]}
		            >
		            	{mode === 'edit' ?
		            	<Select id="inv_status_id"
							className="input-field"
						    options={recordData?.statusList}
		            	/>		            		
		                : <label className="ps-3 dark:text-white">{recordData?.inv_status_name}</label>
		                }
	            	</FormItem>
					<FormItem 
		            	name="inv_type_id"
		                label="Type"
						rules={[requiredField()]}
		            >
		            	{mode === 'edit' ?
		            	<Select id="inv_type_id"
							className="input-field"
		            		options={recordData?.typeList}
		            	/>
		                : <label className="ps-3 dark:text-white">{recordData?.inv_type_name}</label>
		                }
	            	</FormItem>
					<FormItem 
		            	name="inv_forex_id"
		                label="Forex Date"
						rules={[requiredField()]}
		            >
						{mode === 'edit' ?
		            	<Select 
							id="inv_forex_id"
							className="input-field"
		            		options={forexOptions}
							allowClear
							showSearch
							onChange={handleForexChange}
		            	/>
						: <label className="ps-3 dark:text-white">{recordData?.forex_name}</label>
						}
	            	</FormItem>
					<FormItem 
						name="inv_forex_usd"
						label="PHP to USD"
						hidden
					>
						<Input type="hidden" id="inv_forex_usd"/>
					</FormItem>
					<FormItem 
						name="inv_forex_aud"
						label="PHP to AUD"
						rules={[requiredField()]}
					>
						{mode === 'edit' ?
						<InputNumber precision={4}
							id="inv_forex_aud"
							className="input-field aligned-right-number"
							style={{
								width: 150,
							}}
						/>
						:
						<label className="ps-3 dark:text-white">{recordData?.inv_forex_aud}</label>
						}
					</FormItem>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-5 gap-2"> 
					<FormItem extra="col-span-1 md:col-span-2"
		            	name="inv_client_id"
		                label="Client"
						rules={[requiredField()]}
		            >
		            	{mode === 'edit' ?
		            	<Select 
							id="inv_client_id"
							className="input-field"
		            		options={recordData?.clientList}
		            	/>
		                : <label className="ps-3 dark:text-white">{recordData?.inv_client_name}</label>
		                }
	            	</FormItem>  
				</div>
				<div className="grid grid-cols-1 md:grid-cols-5 gap-2"> 
					<FormItem 
		                label="Invoice #"
		            >
		                <label className="ps-3 dark:text-white">{recordData?.inv_number}</label>
	            	</FormItem>
					<FormItem 
						label="Created Date"
						name="created_at"
					>
						{mode !== "view" && recordData?.created_at === null ?
						<DatePicker id="created_at"
							className="input-field"
							format={DATE_FORMAT}
							style={{ width: "100%" }}
						/>
						: 
						<label className="ps-3 dark:text-white">{formatDateTimeOrNull(recordData?.created_at, DATE_FORMAT)}</label>
						}
					</FormItem>
					<FormItem 
						label="Invoice Date"
						name="inv_issue_date"
						rules={[requiredField()]}
					>
						{mode !== "view" ?
						<DatePicker id="inv_issue_date"
							className="input-field"
							format={DATE_FORMAT}
							style={{ width: "100%" }}
						/>
						: 
						<label className="ps-3 dark:text-white">
							{formatDateTimeOrNull(recordData?.inv_issue_date, DATE_FORMAT)}
						</label>
						}
					</FormItem>
					<FormItem 
						label="Due Date"
						name="inv_due_date"
						rules={[requiredField()]}
					>
						{mode !== "view" ?
						<DatePicker id="inv_due_date"
							className="input-field"
							format={DATE_FORMAT}
							style={{ width: "100%" }}
						/>
						: 
						<label className="ps-3 dark:text-white">
							{formatDateTimeOrNull(recordData?.inv_due_date, DATE_FORMAT)}
						</label>
						}
					</FormItem>
	 			</div>		
				<CrudButtons 
					baseRoute="/console/invoices" 
					mode={mode} 
					recordId={recordId} 
					moduleId={MODULE_INVOICES}
					withEdit={recordData?.is_editable}
				/> 			
	 		</Form>	 	
	 	</Card>
  	);
};

const mapStateToProps = (state) => ({
  	recordData: state.invoice.invoiceDetails?.data,
  	requesting: state.invoice.invoiceDetails?.requesting,
  	errors: state.invoice.invoiceDetails?.errors,
	forexList: state.optionlist.forexList?.items || [],
});

const mapDispatchToProps = (dispatch) => ({
	getRecord: (id) => dispatch(getInvoiceDetails(id)),
  	doCreate: (data) => dispatch(doInvoiceCreate(data)),
  	doUpdate: (data) => dispatch(doInvoiceUpdate(data)),
	getForexOptions: (data) => dispatch(getForexList(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceForm);
